/* eslint-disable no-plusplus */
import { pick, extend, chain, isNumber, result, isString, each, isEmpty, clone, has, map, negate, find, isArray } from 'underscore';
import { AssetGroup, AudienceGroupAssetGroupAssociation } from '@bingads-webui/campaign-odata-schemas';
import { createOrUpdateAssetGroup, readAssetGroup } from '@bingads-webui-campaign-react/perf-max-data';
import { processImageListViewModelToModel } from '@bingads-webui-campaign-react/pmax-image-experience';
import {
  getBulkUploadErrorType,
  hasBulkErrorsForEntityType,
  modelToViewModelForBulkErrors,
  removeErrors,
  removeReferencedEntityInvalidIdErrors,
  unexpectedBulkErrorToString,
  isAudienceAssociationDoesntExistError
} from '@bingads-webui-campaign-react/perf-max-step-wizard';
import { getImagesFromMTAssetsAsync } from '@bingads-webui-campaign-react/responsive-ad-editor-utils';
import odata from '@bingads-webui-component-legacy/http/channel/odata';

import { ERROR_ENTITY, genericApiError } from './api-errors';

import { getDefaulAudienceGroup } from '../shared/constants';

const isBulkWrappedResponse = bulkResult => has(bulkResult, 'entity');

const initialAssetGroupData = {
  Status: 'Active',
};

const AssetGroupNegativeReferenceId = -21;

const bulkUpsertAssetGroupSettingsEntityErrorMap = {
  [getBulkUploadErrorType(AssetGroup)]: ERROR_ENTITY.AssetGroup,
  [getBulkUploadErrorType(AudienceGroupAssetGroupAssociation)]: ERROR_ENTITY.AudienceGroupAssetGroupAssociation,
};

export const initialAssetGroupViewModel = {
  data: initialAssetGroupData,
  isValid: false,
  isDirty: false,
};

export const getErrorsPlaceholders = () => ({
  serverErrors: [],
  systemErrors: [],
});


const whitelistedSchemaPropertyKeysForBulkModel = [
  'Campaign', 'Id', 'Name', 'Images',
  'Headlines', 'Status', 'StartDate', 'EndDate',
];

const whitelistedSchemaPropertyKeysForModel = ['AudienceGroup'].concat(whitelistedSchemaPropertyKeysForBulkModel);
const whitelistedSchemaPropertyKeys = whitelistedSchemaPropertyKeysForModel;

const assetsArrayToObject = (assetsArray, propertyPrefix) => {
  const assetObject = {};

  each(assetsArray, (asset, index) => {
    const assetText = chain(asset).result('Asset', {}).result('Text', '').value();
    assetObject[`${propertyPrefix}${index}`] = assetText;
  });

  return assetObject;
};

const modelToViewModelForTextAssets = (assetGroupModel, propertyName, propertyPrefix) => {
  const assetsArray = result(assetGroupModel, propertyName, []);
  const viewModelOverride = {};
  viewModelOverride[`${propertyName}`] = assetsArrayToObject(assetsArray, propertyPrefix);
  return viewModelOverride;
};

const prepareViewModelForImageFetch = (assetGroupModel, cid, aid) => {
  const images = {
    ImageSPOTLIGHT: {
      ImageAssets: null,
      ImageList: null,
    },
    ImagePRONG: {
      ImageAssets: null,
      ImageList: null,
    },
    TransparentLogo: {
      ImageAssets: null,
      ImageList: null,
    },
  };
  if (!assetGroupModel || !assetGroupModel.Images) {
    return images;
  }
  const imageProngModel = assetGroupModel.Images.find(x => x.AssociationType === 'ImagePRONG');
  const imageSpotLightModel = assetGroupModel.Images.find(x => x.AssociationType === 'ImageSPOTLIGHT');
  const transparentLogoModel = assetGroupModel.Images.find(x => x.AssociationType === 'TransparentLogo');

  const imageProngVMPromise = imageProngModel
    ? getImagesFromMTAssetsAsync(odata, `/Customers(${cid})/Accounts(${aid})/` /* baseUrl */, [imageProngModel], false, false, false, true, false)
    : Promise.resolve([]);
  const imageSpotLightVMPromise = imageSpotLightModel
    ? getImagesFromMTAssetsAsync(odata, `/Customers(${cid})/Accounts(${aid})/` /* baseUrl */, [imageSpotLightModel], false, false, false, false, true)
    : Promise.resolve([]);
  const transparentLogoVMPromise = transparentLogoModel
    ? getImagesFromMTAssetsAsync(odata, `/Customers(${cid})/Accounts(${aid})/` /* baseUrl */, [transparentLogoModel], false, false, false, false, false, true)
    : Promise.resolve([]);

  return imageProngVMPromise.then(imageProngVM => imageSpotLightVMPromise.then(imageSpotLightVM => transparentLogoVMPromise.then(transparentLogoVM => ({
    ImageSPOTLIGHT: {
      ImageAssets: null,
      ImageList: imageSpotLightVM,
    },
    ImagePRONG: {
      ImageAssets: null,
      ImageList: imageProngVM,
    },
    TransparentLogo: {
      ImageAssets: null,
      ImageList: transparentLogoVM,
    },
  }))));
};

function parseLoadDate(i18n, dateString) {
  return i18n.parseDate(dateString.substring(0, 10), { raw: 'yyyy-MM-dd' });
}

const modelToViewModelForDateSetting = ({ assetGroupModel, i18n }) => ({
  StartDate: parseLoadDate(i18n, assetGroupModel.StartDate),
  EndDate: assetGroupModel.EndDate ? parseLoadDate(i18n, assetGroupModel.EndDate) : undefined,
});

export const modelToViewModelForAudienceGroup = (assetGroupModel, audienceListMap) => {
  if (assetGroupModel.AudienceGroup) {
    const audiences = chain(assetGroupModel.AudienceGroup.Dimensions)
      .find(dimension => dimension.Type === 'Audience')
      .get('Audiences').value();
    const excludedAudiences = chain(assetGroupModel.AudienceGroup.NegativeDimensions)
      .find(dimension => dimension.Type === 'Audience')
      .get('Audiences').value();
    const interests = map(audiences, audience => find(audienceListMap, audienceMap => audienceMap.id === audience.Id).value);
    return {
      interests,
      excludedAudiences: excludedAudiences || [],
    };
  }
  return {};
};

export const modelToViewModelForAssetGroup = (assetGroupModel, i18n, cid, aid) => {
  let data = pick(assetGroupModel, whitelistedSchemaPropertyKeys);
  data = extend(data, modelToViewModelForTextAssets(assetGroupModel, 'Headlines', 'Headline'));
  data = extend(data, modelToViewModelForTextAssets(assetGroupModel, 'LongHeadlines', 'LongHeadline'));
  data = extend(data, modelToViewModelForTextAssets(assetGroupModel, 'Descriptions', 'Description'));

  data = extend(data, modelToViewModelForDateSetting({
    assetGroupModel,
    i18n,
  }));

  return prepareViewModelForImageFetch(assetGroupModel, cid, aid).then((image) => {
    data = extend(data, image);
    const assetGroupViewModel = {
      data,
      isValid: true,
      isDirty: false,
    };
    return assetGroupViewModel;
  });
};

const formatDateToAPIFormat = (i18n, date) => {
  if (date == null) {
    return '';
  }
  return i18n.formatDate(date, {
    raw: 'M/d/yyyy',
  });
};

const parseDateFromAPIFormat = (i18n, date) => {
  if (date == null) {
    return null;
  }

  if (typeof date === 'string') {
    return i18n.parseDate(date.substring(0, 10), { raw: 'M/d/yyyy' });
  }

  return date;
};

const viewModelToBulkModelForDateSetting = (i18n, assetGroupViewModel) => {
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);
  return {
    // eslint-disable-next-line new-cap
    StartDate: assetGroupViewModel.data.StartDate ? formatDateToAPIFormat(i18n, assetGroupViewModel.data.StartDate) : formatDateToAPIFormat(i18n, tomorrow),
    EndDate: assetGroupViewModel.data.EndDate ? formatDateToAPIFormat(i18n, assetGroupViewModel.data.EndDate) : '1/1/3000',
  };
};

const bulkModelToViewModelForDateSetting = ({ assetGroupModel, i18n }) => ({
  StartDate: parseDateFromAPIFormat(i18n, assetGroupModel.StartDate),
  EndDate: assetGroupModel.EndDate ? parseDateFromAPIFormat(i18n, assetGroupModel.EndDate) : undefined,
});

const textAssetPropertyNameRegEx = /([^\d]+)(\d+)/;
const parseAssetPropertyName = (propertyName) => {
  const matches = propertyName.match(textAssetPropertyNameRegEx);
  return matches !== null ?
    ({ name: matches[1], index: parseInt(matches[2], 10) }) :
    ({ name: 'invalid property', index: NaN });
};

const assetsObjectToArray = (assetsObject, propertyPrefix) =>
  chain(assetsObject)
    .pairs()
    .filter(([, value]) => !isEmpty(value))
    .map(([key, value]) => [parseAssetPropertyName(key), value])
    .filter(([{ name, index }]) => name === propertyPrefix && isNumber(index))
    .value();

const assetToModel = ([, value]) => ({
  Asset: {
    Text: value,
  },
});

const viewModelToBulkModelForTextAssets = (assetGroupViewModel, propertyName, vmPropertyPrefix) => ({
  [propertyName]:
    assetsObjectToArray(result(assetGroupViewModel, propertyName, {}), vmPropertyPrefix)
      .map(assetToModel),
});

const viewModelToBulkModelForImages = (assetGroupViewModel, serverContext, aid, cid) => {
  const ImageList = [];
  if (assetGroupViewModel.ImageSPOTLIGHT && assetGroupViewModel.ImageSPOTLIGHT.ImageList) {
    ImageList.push(...assetGroupViewModel.ImageSPOTLIGHT.ImageList);
  }
  if (assetGroupViewModel.ImagePRONG && assetGroupViewModel.ImagePRONG.ImageList) {
    ImageList.push(...assetGroupViewModel.ImagePRONG.ImageList);
  }
  if (assetGroupViewModel.TransparentLogo && assetGroupViewModel.TransparentLogo.ImageList) {
    ImageList.push(...assetGroupViewModel.TransparentLogo.ImageList);
  }
  const model = processImageListViewModelToModel(ImageList, serverContext, aid, cid);
  return model;
};

const isAudienceSignalAssetGroupAssociationUpdateScenario = originalAssetGroupViewModel =>
  !!result(originalAssetGroupViewModel.AudienceGroup, 'Id');
const isAudienceSignalAssetGroupAssociationDeleteScenario = (audiencesSignal, originalAssetGroupViewModel) =>
  !audiencesSignal.newAudiences &&
  !!result(originalAssetGroupViewModel.AudienceGroup, 'Id');

const viewModelToBulkModelForAudienceGroup = (assetGroupViewModel, originalAssetGroupViewModel, audiencesSignal, i18n) => {
  if (!audiencesSignal) {
    return {};
  }
  const { interests, excludedAudiences, valueToAudience } = audiencesSignal;
  const { Id = AssetGroupNegativeReferenceId } = assetGroupViewModel;
  const { AudienceGroup: OriginalAudienceGroup } = originalAssetGroupViewModel;
  let model = {};
  if (isAudienceSignalAssetGroupAssociationUpdateScenario(originalAssetGroupViewModel)) {
    if (interests || excludedAudiences) {
      model = extend({
        AudienceGroup: {
          ...OriginalAudienceGroup,
          Audiences: interests ? map(interests, newAudience => valueToAudience[newAudience]) : [],
          NegativeAudiences: excludedAudiences && isArray(excludedAudiences) ? excludedAudiences : [],
          AgeRanges: ['Unknown', 'EighteenToTwentyFour', 'TwentyFiveToThirtyFour', 'ThirtyFiveToFourtyNine', 'FiftyToSixtyFour', 'SixtyFiveAndAbove'],
          GenderTypes: ['Unknown', 'Male', 'Female'],
        },
      });
    }
  } else if (isAudienceSignalAssetGroupAssociationDeleteScenario(assetGroupViewModel, originalAssetGroupViewModel)) {
    model = extend({
      AudienceGroupAssetGroupAssociations: [{
        AudienceGroupId: OriginalAudienceGroup.Id,
        AssetGroupId: Id,
        Status: 'Deleted',
      }],
    });
  } else {
    if (!interests && !excludedAudiences) {
      return model;
    }
    const defaultAudienceGroup = getDefaulAudienceGroup(i18n);
    model = extend({
      AudienceGroup: {
        ...defaultAudienceGroup,
        Audiences: interests ? map(interests, newAudience => valueToAudience[newAudience]) : [],
        NegativeAudiences: excludedAudiences && isArray(excludedAudiences) ? excludedAudiences : [],
      },
      AudienceGroupAssetGroupAssociations: [{
        AudienceGroupId: defaultAudienceGroup.Id,
        AssetGroupId: Id,
        Status: 'Active',
      }],
    });
  }
  return model;
};

const viewModelToBulkModelForAssetGroup = (i18n, serverContext, cid, aid, assetGroupViewModel, originalAssetGroup, audiencesSignal) => {
  let model = pick(assetGroupViewModel.data, whitelistedSchemaPropertyKeysForBulkModel);
  if (!model.Id) {
    model.Id = AssetGroupNegativeReferenceId;
  }
  model = extend(model, viewModelToBulkModelForTextAssets(assetGroupViewModel.data, 'Headlines', 'Headline'));

  model = extend(model, viewModelToBulkModelForDateSetting(i18n, assetGroupViewModel));

  model = extend(model, viewModelToBulkModelForAudienceGroup(assetGroupViewModel.data, originalAssetGroup.data, audiencesSignal, i18n));

  // return Promise.resolve(model);
  return viewModelToBulkModelForImages(assetGroupViewModel.data, serverContext, cid, aid)
    .then(imagesModel => extend(model, imagesModel));
};

export const viewModelToModelForAssetGroup = (assetGroupViewModel) => {
  const model = pick(assetGroupViewModel.data, whitelistedSchemaPropertyKeysForModel);
  if (!assetGroupViewModel.data.Id) {
    model.Id = AssetGroupNegativeReferenceId;
  }
  return model;
};

export const loadAssetGroup = (cid, aid, campaignId, assetGroupId, i18n) =>
  readAssetGroup(cid, aid, campaignId, assetGroupId).then(assetGroupModel => modelToViewModelForAssetGroup(assetGroupModel, i18n, cid, aid));

const bulkModelToViewModelForAssetGroup = ({
  i18n, scenario, activityLogger,
  assetGroupViewModel, // the asset group's view model the user tried to save
  originalAssetGroupViewModel, // the asset group's view model before the save attempt
  assetGroupModel, // the asset group's model as was generated off of assetGroupViewModel and processed by bulk infrastructure
  errors = [], // the bulk upsert errors
}) => {
  let data;
  let newOriginalAssetGroupViewModel;

  const hasAssetGroupErrors = hasBulkErrorsForEntityType(errors, getBulkUploadErrorType(AssetGroup));
  const hasAudienceSignalAssetGroupAssociationErrors = hasBulkErrorsForEntityType(errors, getBulkUploadErrorType(AudienceGroupAssetGroupAssociation), negate(isAudienceAssociationDoesntExistError));
  if (!hasAssetGroupErrors) {
    data = pick(assetGroupModel, whitelistedSchemaPropertyKeys);
    // Correct the type of the AssetGroup's ID:
    if (data.Id && isString(data.Id)) {
      // eslint-disable-next-line no-param-reassign
      data.Id = parseInt(data.Id, 10);
    }
    data = extend(data, modelToViewModelForTextAssets(assetGroupModel, 'Headlines', 'Headline'));
    data = extend(data, modelToViewModelForTextAssets(assetGroupModel, 'LongHeadlines', 'LongHeadline'));
    data = extend(data, modelToViewModelForTextAssets(assetGroupModel, 'Descriptions', 'Description'));
    data = extend(data, bulkModelToViewModelForDateSetting({ assetGroupModel, i18n }));

    newOriginalAssetGroupViewModel = clone(assetGroupViewModel);
  } else {
    ({ data } = assetGroupViewModel);
    newOriginalAssetGroupViewModel = originalAssetGroupViewModel;

    // If AsetGroup change was not accepted, but audience signal association change was, then
    // we need to update the model to reflect the new state of audience signal association or, otherwise,
    // when the user was removing the association MT will return the error that the association is not found and the user will be blocked from saving the changes.
    // For the association creation scenario, MT doesn't generate errors if we request the create an association that already exist.
    if (!hasAudienceSignalAssetGroupAssociationErrors) {
      if (isAudienceSignalAssetGroupAssociationDeleteScenario(assetGroupViewModel.data, originalAssetGroupViewModel.data)) {
        delete newOriginalAssetGroupViewModel.data.AudienceGroup;
      } else {
        newOriginalAssetGroupViewModel.data.AudienceGroup = assetGroupViewModel.data.AudienceGroup;
      }
    }
  }

  data = extend(data, pick(assetGroupViewModel.data, 'Images', 'FinalUrlForRecommendation'));

  const newAssetGroupViewModel = {
    data,
    isValid: true,
    isDirty: false,
  };

  let filteredErrors = errors;
  if (newAssetGroupViewModel.data.Id <= 0) {
    // Handling the common creation scenario where the main entity didn't get created. In this case all related entities will not be created as well and will generate errors.
    filteredErrors = removeReferencedEntityInvalidIdErrors(errors);
  }

  // Handling the concurrency scenario where while the user was deleting the association somebody else already deleted it
  filteredErrors = removeErrors(filteredErrors, isAudienceAssociationDoesntExistError);

  const serverErrors = modelToViewModelForBulkErrors(
    filteredErrors,
    bulkUpsertAssetGroupSettingsEntityErrorMap,
    scenario,
    i18n,
    activityLogger
  );

  return { assetGroupViewModel: newAssetGroupViewModel, originalAssetGroupViewModel: newOriginalAssetGroupViewModel, serverErrors };
};

export const saveAssetGroupAndLinkedData = (
  {
    assetGroup: assetGroupViewModel,
    originalAssetGroup: originalAssetGroupViewModel,
    campaign,
    audiencesSignal,
  },
  {
    scenario,
    i18n,
    activityLogger,
    serverContext,
    cid,
    aid,
  }
) => viewModelToBulkModelForAssetGroup(i18n, serverContext, cid, aid, assetGroupViewModel, originalAssetGroupViewModel, audiencesSignal)
  .then((bulkModel) => {
    const modelToSave = {
      ...bulkModel,
      Campaign: pick(campaign, 'Id'),
    };
    return createOrUpdateAssetGroup(modelToSave);
  })
  .then((assetGroupModel) => {
    const { assetGroupViewModel: assetGroup, originalAssetGroupViewModel: originalAssetGroup } = bulkModelToViewModelForAssetGroup({
      i18n, scenario, activityLogger, assetGroupViewModel, originalAssetGroupViewModel, assetGroupModel,
    });
    const data = {
      assetGroup,
      originalAssetGroup,
      ...getErrorsPlaceholders(),
    };

    return data;
  }, (bulkOrSystemError) => {
    if (isBulkWrappedResponse(bulkOrSystemError)) {
      const { entity: assetGroupModel, errors } = bulkOrSystemError;
      const { assetGroupViewModel: assetGroup, originalAssetGroupViewModel: originalAssetGroup, serverErrors } = bulkModelToViewModelForAssetGroup({
        i18n,
        scenario,
        activityLogger,
        assetGroupViewModel,
        originalAssetGroupViewModel,
        assetGroupModel,
        errors,
      });

      // eslint-disable-next-line prefer-promise-reject-errors
      const res = {
        assetGroup,
        originalAssetGroup,
        ...getErrorsPlaceholders(),
        serverErrors,
      };
      if (isEmpty(serverErrors)) {
        return Promise.resolve(res);
      }
      return Promise.reject(res);
    }

    // Activity
    // eslint-disable-next-line no-console
    console.warn(`Asset group bulk upsert system error: ${unexpectedBulkErrorToString(bulkOrSystemError)}`);
    // eslint-disable-next-line prefer-promise-reject-errors
    return Promise.reject({
      assetGroup: assetGroupViewModel,
      originalAssetGroup: originalAssetGroupViewModel,
      ...getErrorsPlaceholders(),
      serverErrors: [genericApiError],
    });
  });
